import CTASection from "@/components/CTASection";
import {
  CATEGORY_BY_SLUG,
  OFFICE_CATEGORIES,
  OFFICE_BUILT_FOR_SCALE,
  OFFICE_MATERIALS,
} from "@/lib/siteData";

const OfficePage = () => {
  const category = CATEGORY_BY_SLUG["office"];

  return (
    <div data-testid="office-page">
      {/* HERO */}
      <section className="relative">
        <div className="relative h-[80vh] min-h-[560px] overflow-hidden">
          <img
            src={category.hero}
            alt="Modular office furniture — workstations, desks, storage"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/30 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-7xl mx-auto w-full px-5 md:px-10 pb-14 md:pb-24">
              <div className="max-w-3xl text-white">
                <div className="inline-block bg-black/50 backdrop-blur-sm px-3 py-1.5 mb-5">
                  <div className="overline" style={{ color: "#F8D16F" }}>
                    Office &amp; workstations
                  </div>
                </div>
                <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight font-light">
                  Workspaces<br />
                  <span className="italic">That Perform.</span>
                </h1>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 reveal">
            <div className="overline mb-4">Overview</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight font-light">
              Ergonomic. Modular.<br />
              <span className="italic text-teal">Built to scale.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 reveal space-y-5 text-lg md:text-xl text-black/75 leading-relaxed">
            <p>
              Office and commercial workstation solutions — designed for
              corporate offices, co-working spaces, and home offices. Every
              module is drawn ergonomically, engineered for continuous use, and
              built to slot into a repeatable floor-plan.
            </p>
            <p>
              Wired for modern power and data, standardised for
              multi-location roll-outs, and delivered on schedule — whether it's
              a single cabin or a 400-seat floor across four cities.
            </p>
          </div>
        </div>
      </section>

      {/* PRODUCT CATEGORIES */}
      <section
        data-testid="office-categories"
        className="bg-cream py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-16 reveal">
            <div className="max-w-2xl">
              <div className="overline mb-4">Product categories</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
                Six systems,<br />
                <span className="italic text-navy">one floor plan.</span>
              </h2>
            </div>
            <p className="md:text-right md:max-w-sm text-black/60">
              Every office is a mix of these six families. Specify each once,
              order any quantity, install in any city.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {OFFICE_CATEGORIES.map((c, i) => (
              <div
                key={c.name}
                data-testid={`office-category-${c.name.toLowerCase().replace(/[\s&/]+/g, "-")}`}
                className="reveal group"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="aspect-[4/5] overflow-hidden bg-ivory">
                  <img
                    src={c.image}
                    alt={c.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                  />
                </div>
                <div className="mt-5">
                  <div className="font-display text-2xl leading-tight text-navy">
                    {c.name}
                  </div>
                  <p className="mt-2 text-sm md:text-base text-black/70 leading-relaxed">
                    {c.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUILT FOR SCALE */}
      <section
        data-testid="office-built-for-scale"
        className="bg-ivory py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl mb-12 md:mb-16 reveal">
            <div className="overline mb-4">Built for scale</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
              One BOM,<br />
              <span className="italic text-teal">many cities.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-black/70 leading-relaxed">
              Corporate rollouts — 20 seats or 2,000 — need repeatable systems,
              not one-off pieces. Our office range is engineered for that
              standardisation from day one.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {OFFICE_BUILT_FOR_SCALE.map((h, i) => (
              <div
                key={h.title}
                className="reveal bg-white border-l-4 border-navy p-6 md:p-7"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="overline mb-2">0{i + 1}</div>
                <div className="font-display text-xl md:text-2xl leading-tight text-navy">
                  {h.title}
                </div>
                <p className="mt-3 text-sm md:text-base text-black/70 leading-relaxed">
                  {h.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MATERIALS & FINISHES */}
      <section
        data-testid="office-materials"
        className="bg-cream py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl mb-12 md:mb-16 reveal">
            <div className="overline mb-4">Materials &amp; finishes</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight font-light">
              Commercial-grade<br />
              <span className="italic text-navy">as standard.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-black/70 leading-relaxed">
              Every office component uses boards, finishes, and hardware rated
              for continuous daily use — engineered to look right for years and
              service without downtime.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Boards */}
            <div className="reveal bg-white border-t-4 border-teal p-8 md:p-10">
              <div className="overline mb-3">Board options</div>
              <div className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-navy">
                Structural core
              </div>
              <ul className="mt-5 space-y-3 text-base text-black/80">
                {OFFICE_MATERIALS.boards.map((b) => (
                  <li
                    key={b}
                    className="flex gap-3 items-center border-t border-brandgray pt-3"
                  >
                    <span className="w-1.5 h-1.5 bg-teal shrink-0" />
                    <span className="font-medium">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Finishes */}
            <div
              className="reveal bg-white border-t-4 border-coral p-8 md:p-10"
              style={{ transitionDelay: "80ms" }}
            >
              <div className="overline mb-3">Surface finishes</div>
              <div className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-navy">
                Laminate &amp; Veneer
              </div>
              <ul className="mt-5 space-y-3 text-base text-black/80">
                {OFFICE_MATERIALS.finishes.map((f) => (
                  <li key={f.name} className="border-t border-brandgray pt-3">
                    <div className="font-medium">{f.name}</div>
                    <div className="text-sm text-teal mt-0.5">{f.specs}</div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Hardware */}
            <div
              className="reveal bg-white border-t-4 border-gold p-8 md:p-10"
              style={{ transitionDelay: "160ms" }}
            >
              <div className="overline mb-3">Office hardware</div>
              <div className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-navy">
                Locks &amp; cable management
              </div>
              <ul className="mt-5 space-y-3 text-base text-black/80">
                {OFFICE_MATERIALS.hardware.map((h) => (
                  <li
                    key={h}
                    className="border-t border-brandgray pt-3 font-medium"
                  >
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA — bulk / multi-location */}
      <CTASection
        eyebrow="Corporate rollout?"
        title="Discuss a Bulk Order."
        body="Multi-location fit-outs, phased seat counts, timing across cities — send us a rough scope and we'll come back with a proposal and a delivery calendar."
        primaryLabel="Discuss a Bulk/Multi-Location Order"
        primaryTo="/contact"
        secondaryLabel="Contact Us"
        secondaryTo="/contact"
        testid="office-cta"
      />
    </div>
  );
};

export default OfficePage;
